import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  LogIn, 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  MapPin,
  Calendar,
  Languages,
  DollarSign,
  User,
  LogOut,
  Upload,
  Receipt,
  Send,
  AlertCircle,
  Loader2,
  ClipboardCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VendorPortal = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [offers, setOffers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [completedAssignments, setCompletedAssignments] = useState([]);
  const [submittedInvoices, setSubmittedInvoices] = useState([]);
  const [earnings, setEarnings] = useState({ total: 0, pending: 0 });
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [activeTab, setActiveTab] = useState('offers');
  
  // Invoice submission state
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_amount: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [submittingInvoice, setSubmittingInvoice] = useState(false);

  // Check for existing session
  useEffect(() => {
    const vendorData = localStorage.getItem('vendor_portal_user');
    if (vendorData) {
      const parsed = JSON.parse(vendorData);
      setVendor(parsed);
      setIsLoggedIn(true);
      fetchVendorData(parsed.vendor_id);
    }
  }, []);

  const fetchVendorData = async (vendorId) => {
    setLoading(true);
    try {
      // Fetch pending offers and assignments
      const offersRes = await fetch(`${API_URL}/api/vendor-portal/offers/${vendorId}`);
      if (offersRes.ok) {
        const data = await offersRes.json();
        setOffers(data.offers || []);
        setAssignments(data.assignments || []);
        setEarnings(data.earnings || { total: 0, pending: 0 });
      }
      
      // Fetch assignments awaiting invoice
      const awaitingRes = await fetch(`${API_URL}/api/vendor-portal/assignments-awaiting-invoice/${vendorId}`);
      if (awaitingRes.ok) {
        const data = await awaitingRes.json();
        setCompletedAssignments(data || []);
      }
      
      // Fetch submitted invoices
      const invoicesRes = await fetch(`${API_URL}/api/vendor-portal/invoices/${vendorId}`);
      if (invoicesRes.ok) {
        const data = await invoicesRes.json();
        setSubmittedInvoices(data || []);
      }
    } catch (error) {
      console.error('Error fetching vendor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/vendor-portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setVendor(data.vendor);
        setIsLoggedIn(true);
        localStorage.setItem('vendor_portal_user', JSON.stringify(data.vendor));
        fetchVendorData(data.vendor.vendor_id);
        toast.success('Welcome back!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Login failed');
      }
    } catch (error) {
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('vendor_portal_user');
    setIsLoggedIn(false);
    setVendor(null);
    setOffers([]);
    setAssignments([]);
  };

  const handleOfferResponse = async (offerId, accept) => {
    try {
      const res = await fetch(`${API_URL}/api/broadcast/offer/${offerId}/respond?accept=${accept}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchVendorData(vendor.vendor_id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to respond');
      }
    } catch (error) {
      toast.error('Failed to respond to offer');
    }
  };

  const openInvoiceDialog = (assignment) => {
    setSelectedAssignment(assignment);
    setInvoiceForm({
      invoice_amount: assignment.total_pay || '',
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setInvoiceDialogOpen(true);
  };

  const handleSubmitInvoice = async () => {
    if (!invoiceForm.invoice_amount || parseFloat(invoiceForm.invoice_amount) <= 0) {
      toast.error('Please enter a valid invoice amount');
      return;
    }

    setSubmittingInvoice(true);
    try {
      const res = await fetch(`${API_URL}/api/vendor-portal/submit-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assignment_id: selectedAssignment.assignment_id,
          work_order_id: selectedAssignment.work_order_id,
          invoice_amount: parseFloat(invoiceForm.invoice_amount),
          invoice_number: invoiceForm.invoice_number || null,
          invoice_date: invoiceForm.invoice_date,
          notes: invoiceForm.notes || null
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setInvoiceDialogOpen(false);
        fetchVendorData(vendor.vendor_id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit invoice');
      }
    } catch (error) {
      toast.error('Failed to submit invoice');
    } finally {
      setSubmittingInvoice(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      declined: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
      expired: 'bg-gray-100 text-gray-800',
      pending_review: 'bg-orange-100 text-orange-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending_review: 'Under Review',
      approved: 'Approved',
      rejected: 'Rejected'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{labels[status] || status}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // Count assignments awaiting invoice (completed but no invoice submitted)
  const awaitingInvoiceCount = completedAssignments.filter(a => !a.invoice_submitted).length;

  // Login Page
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_457f63c4-6a2a-4995-8167-0b04779a6e2e/artifacts/c21k9zo8_bylyngo%20interpreting.png" 
              alt="Bylyngo"
              className="h-20 w-auto mx-auto mb-4"
            />
            <CardTitle className="text-2xl">Vendor Portal</CardTitle>
            <p className="text-muted-foreground">Sign in to view assignments and submit invoices</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                  data-testid="vendor-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  data-testid="vendor-password-input"
                />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="vendor-login-btn">
                <LogIn className="h-4 w-4 mr-2" />
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_457f63c4-6a2a-4995-8167-0b04779a6e2e/artifacts/c21k9zo8_bylyngo%20interpreting.png" 
              alt="Bylyngo"
              className="h-10 w-auto"
            />
            <div>
              <h1 className="font-semibold text-lg">Vendor Portal</h1>
              <p className="text-sm text-muted-foreground">Welcome, {vendor?.name}</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="vendor-logout-btn">
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{offers.filter(o => o.status === 'pending').length}</p>
                  <p className="text-sm text-muted-foreground">Pending Offers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{assignments.filter(a => a.status === 'accepted').length}</p>
                  <p className="text-sm text-muted-foreground">Active Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={awaitingInvoiceCount > 0 ? 'ring-2 ring-orange-400' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <Receipt className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{awaitingInvoiceCount}</p>
                  <p className="text-sm text-muted-foreground">Awaiting Invoice</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{submittedInvoices.length}</p>
                  <p className="text-sm text-muted-foreground">Invoices Sent</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">${earnings.total?.toLocaleString() || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Paid</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alert for pending invoices */}
        {awaitingInvoiceCount > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-orange-800">You have {awaitingInvoiceCount} completed assignment(s) awaiting invoice submission</p>
              <p className="text-sm text-orange-600">Submit your invoices to get paid promptly.</p>
            </div>
            <Button 
              size="sm" 
              className="ml-auto bg-orange-600 hover:bg-orange-700"
              onClick={() => setActiveTab('invoices')}
            >
              Submit Invoice
            </Button>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="offers" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Available Jobs
            </TabsTrigger>
            <TabsTrigger value="assignments" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              My Assignments
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2 relative">
              <Receipt className="h-4 w-4" />
              Invoices
              {awaitingInvoiceCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                  {awaitingInvoiceCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Available Jobs Tab */}
          <TabsContent value="offers">
            <Card>
              <CardHeader className="bg-yellow-50">
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  Available Assignments
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {offers.filter(o => o.status === 'pending').length > 0 ? (
                  <div className="space-y-4">
                    {offers.filter(o => o.status === 'pending').map((offer) => (
                      <div key={offer.offer_id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-blue-100 text-blue-800">{offer.work_order_id}</Badge>
                              {getStatusBadge(offer.status)}
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div className="flex items-center gap-2">
                                <Languages className="h-4 w-4 text-gray-400" />
                                <span>{offer.languages?.join(', ') || 'N/A'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <span>{offer.start_datetime || 'TBD'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-gray-400" />
                                <span>{offer.location || 'Remote'}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleOfferResponse(offer.offer_id, true)}
                              data-testid={`accept-offer-${offer.offer_id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Accept
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleOfferResponse(offer.offer_id, false)}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Decline
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No pending offers at this time</p>
                    <p className="text-sm">Check back soon for new assignments!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Assignments Tab */}
          <TabsContent value="assignments">
            <Card>
              <CardHeader className="bg-green-50">
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Your Assignments
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {assignments.length > 0 ? (
                  <div className="space-y-3">
                    {assignments.map((assignment) => (
                      <div key={assignment.assignment_id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-blue-100 text-blue-800">{assignment.work_order_id}</Badge>
                              {getStatusBadge(assignment.status)}
                            </div>
                            <p className="text-sm text-muted-foreground">{assignment.role}</p>
                          </div>
                          <p className="font-semibold">${assignment.total_pay?.toLocaleString() || 0}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No assignments yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <div className="space-y-6">
              {/* Awaiting Invoice Section */}
              <Card>
                <CardHeader className="bg-orange-50">
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-orange-600" />
                    Submit Invoice for Completed Work
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  {completedAssignments.filter(a => !a.invoice_submitted).length > 0 ? (
                    <div className="space-y-4">
                      {completedAssignments.filter(a => !a.invoice_submitted).map((assignment) => (
                        <div key={assignment.assignment_id} className="border border-orange-200 bg-orange-50 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Badge className="bg-blue-100 text-blue-800">{assignment.work_order_id}</Badge>
                                <Badge className="bg-green-100 text-green-800">Completed</Badge>
                                <Badge className="bg-orange-100 text-orange-800">Invoice Needed</Badge>
                                {assignment.work_order_details?.interpreting_details?.modality === 'onsite' && !assignment.encounter_id && (
                                  <Badge className="bg-purple-100 text-purple-800">Encounter Form Needed</Badge>
                                )}
                              </div>
                              <div className="text-sm space-y-1">
                                <p><strong>Client:</strong> {assignment.work_order_details?.client_name || 'N/A'}</p>
                                <p><strong>Languages:</strong> {assignment.work_order_details?.languages?.join(', ') || 'N/A'}</p>
                                <p><strong>Role:</strong> {assignment.role}</p>
                                {assignment.work_order_details?.interpreting_details?.location && (
                                  <p className="flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    <strong>Location:</strong> {assignment.work_order_details.interpreting_details.location}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              {assignment.work_order_details?.interpreting_details?.modality === 'onsite' && !assignment.encounter_id && (
                                <Button 
                                  variant="outline"
                                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                                  onClick={() => navigate(`/encounter-form/${assignment.assignment_id}`)}
                                  data-testid={`encounter-form-${assignment.assignment_id}`}
                                >
                                  <ClipboardCheck className="h-4 w-4 mr-2" />
                                  Encounter Form
                                </Button>
                              )}
                              <Button 
                                className="bg-orange-600 hover:bg-orange-700"
                                onClick={() => openInvoiceDialog(assignment)}
                                data-testid={`submit-invoice-${assignment.assignment_id}`}
                              >
                                <Send className="h-4 w-4 mr-2" />
                                Submit Invoice
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-400" />
                      <p>All invoices submitted!</p>
                      <p className="text-sm">No pending invoice submissions.</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Submitted Invoices Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    Invoice History
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  {submittedInvoices.length > 0 ? (
                    <div className="space-y-3">
                      {submittedInvoices.map((invoice) => (
                        <div key={invoice.vendor_invoice_id} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge className="bg-blue-100 text-blue-800">{invoice.work_order_id}</Badge>
                                {getStatusBadge(invoice.status)}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                Invoice #{invoice.invoice_number || invoice.vendor_invoice_id} • 
                                Submitted {formatDate(invoice.submitted_at)}
                              </p>
                              {invoice.review_notes && (
                                <p className="text-sm text-orange-600">Note: {invoice.review_notes}</p>
                              )}
                            </div>
                            <p className="text-xl font-bold text-green-600">${invoice.invoice_amount?.toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No invoices submitted yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Invoice Submission Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-orange-600" />
              Submit Invoice
            </DialogTitle>
          </DialogHeader>
          
          {selectedAssignment && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-2">Submitting invoice for:</p>
                <p className="font-semibold">{selectedAssignment.work_order_id}</p>
                <p className="text-sm">{selectedAssignment.work_order_details?.client_name}</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="invoice_amount">Invoice Amount ($) *</Label>
                  <Input
                    id="invoice_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={invoiceForm.invoice_amount}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="invoice-amount-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="invoice_number">Your Invoice Number (Optional)</Label>
                  <Input
                    id="invoice_number"
                    value={invoiceForm.invoice_number}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })}
                    placeholder="INV-001"
                    data-testid="invoice-number-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="invoice_date">Invoice Date</Label>
                  <Input
                    id="invoice_date"
                    type="date"
                    value={invoiceForm.invoice_date}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })}
                    data-testid="invoice-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={invoiceForm.notes}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                    placeholder="Any additional notes..."
                    rows={3}
                    data-testid="invoice-notes-input"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={handleSubmitInvoice}
              disabled={submittingInvoice}
              data-testid="submit-invoice-confirm-btn"
            >
              {submittingInvoice ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Invoice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorPortal;
